import { ShieldCheck } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

import { CardActions, createCardActionsProps } from "@/src/components/cards/card-actions";
import { EntityDropdownMenu, Field, FieldLayout, hasConflicts, renderBadge } from "@/src/components/cards/common";
import CoverageCardEditable from "@/src/components/cards/coverage-card/coverage-card-editable";
import { ContextMenu, ContextMenuTrigger } from "@/src/components/shadcn/ui/context-menu";
import { ValerieCard, type ValerieCardHeaderVariant } from "@/src/components/valerie-card";
import { useUser } from "@/src/context/user-context";
import { canWriteSOR } from "@/src/lib/rbac";
import { cn } from "@/src/lib/shadcn-utils";
import { Coverage, CoverageType } from "@/src/models/coverage";

const COVERAGE_FIELDS: Field[] = [
  { key: "payer", label: "Payer", comparable: true, section: "Plan" },
  { key: "plan", label: "Plan Name", comparable: true, section: "Plan" },
  { key: "lineOfBusiness", label: "Line of Business", comparable: true, section: "Plan" },
  { key: "effectiveDate", label: "Effective Date", type: "date", comparable: true, section: "Plan" },
  { key: "expirationDate", label: "Expiration Date", type: "date", comparable: false, section: "Plan" },
  { key: "subscriberId", label: "Subscriber ID", comparable: true, section: "Subscriber" },
  { key: "groupNumber", label: "Group Number", comparable: true, section: "Subscriber" },
  { key: "subscriberGivenName", label: "Subscriber Given Name", comparable: true, section: "Subscriber" },
  { key: "subscriberFamilyName", label: "Subscriber Family Name", comparable: true, section: "Subscriber" },
  { key: "subscriberDateOfBirth", label: "Subscriber DOB", type: "date", comparable: true, section: "Subscriber" },
  { key: "relationshipToSubscriber", label: "Relationship to Subscriber", comparable: true, section: "Subscriber" },
  { key: "phoneNumber", label: "Phone Number", type: "tel", comparable: false, section: "Contact" },
  { key: "faxPhoneNumber", label: "Fax Phone Number", type: "tel", comparable: false, section: "Contact" },
];

export default function CoverageCard({
  coverage,
  type,
  isEditable = false,
  compareMode = false,
  secondaryCoverage = null,
  eligibilityStatus = null,
  headerActions,
  className,
  variant = "outline",
  onCoverageSaved,
}: {
  coverage: Coverage;
  type: "Primary" | "Secondary";
  isEditable?: boolean;
  compareMode?: boolean;
  secondaryCoverage?: Coverage | null;
  eligibilityStatus?: "active" | "inactive" | null;
  headerActions?: ReactNode;
  className?: string;
  variant?: ValerieCardHeaderVariant;
  onCoverageSaved?: (coverage: Coverage) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [displayCoverage, setDisplayCoverage] = useState(coverage);
  const { roles } = useUser();
  const canWrite = canWriteSOR(roles);
  const effectiveIsEditable = isEditable && canWrite;

  useEffect(() => {
    setDisplayCoverage(coverage);
  }, [coverage]);

  if (!displayCoverage) {
    return null;
  }

  if (isEditing) {
    return (
      <CoverageCardEditable
        coverage={displayCoverage}
        type={type}
        onClose={(savedCoverage) => {
          if (savedCoverage) {
            setDisplayCoverage(savedCoverage);
            onCoverageSaved?.(savedCoverage);
          }

          setIsEditing(false);
        }}
      />
    );
  }

  let hasAnyConflicts = false;

  if (compareMode && secondaryCoverage) {
    hasAnyConflicts = hasConflicts(COVERAGE_FIELDS, displayCoverage, secondaryCoverage);
  }

  const cardActionsProps = createCardActionsProps({
    id: displayCoverage.id,
    label: "Coverage ID",
    customProps: [{ label: "Edit Coverage", onClick: () => setIsEditing(true) }],
  });

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div className="w-full outline-none" tabIndex={0}>
          <ValerieCard
            title={`${type} Coverage`}
            icon={<ShieldCheck />}
            className={cn("w-full overflow-hidden", className)}
            variant={variant}
            contentClassName="space-y-4"
            actions={
              <>
                {headerActions}
                {eligibilityStatus === "active" && (
                  <span className="rounded-full bg-success/20 px-2 py-0.5 text-xs font-medium text-success">
                    Eligible
                  </span>
                )}
                {eligibilityStatus === "inactive" && (
                  <span className="rounded-full bg-destructive/20 px-2 py-0.5 text-xs font-medium text-destructive">
                    Ineligible
                  </span>
                )}
                {renderBadge(compareMode, secondaryCoverage, hasAnyConflicts)}
                {effectiveIsEditable && <EntityDropdownMenu cardActionsProps={cardActionsProps} />}
              </>
            }
          >
            {displayCoverage.coverageType === CoverageType.SELF_PAY ? (
              <div className="text-sm">
                <span className="font-semibold">Coverage Type:</span> Self Pay
              </div>
            ) : (
              <FieldLayout
                fields={COVERAGE_FIELDS}
                patient={displayCoverage}
                secondaryObject={secondaryCoverage}
                compareMode={compareMode}
              />
            )}
          </ValerieCard>
        </div>
      </ContextMenuTrigger>
      {effectiveIsEditable && <CardActions {...cardActionsProps} isContextMenu={true} />}
    </ContextMenu>
  );
}
